import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Sparkles, Loader2, Save } from "lucide-react";

export default function LoginExperience() {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/login-experience/admin").then(({ data }) => setC(data)).catch((e) =>
      toast.error(e.response?.data?.detail || "Load failed"));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/login-experience/admin", {
        scenes: c.scenes, brand_mode: c.brand_mode,
        brands: (c.brands || []), proof: (c.proof || []),
        motion: c.motion, show_ribbon: c.show_ribbon, show_proof: c.show_proof,
      });
      toast.success("Login experience saved — refresh the login page to see it");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  if (!c) return <div className="p-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const upScene = (i, k, v) => setC((s) => ({ ...s, scenes: s.scenes.map((sc, j) => j === i ? { ...sc, [k]: v } : sc) }));
  const moveScene = (i, dir) => setC((s) => {
    const j = i + dir;
    if (j < 0 || j >= s.scenes.length) return s;
    const arr = [...s.scenes];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...s, scenes: arr };
  });

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="login-experience-page">
      <div className="flex items-start justify-between mb-5">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900 flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-indigo-700" /> Login Experience
          </h1>
          <p className="text-sm text-slate-500">Configuration Studio · Experience. Edit the login story scenes, brand ribbon and proof strip — no developer needed.</p>
        </div>
        <Button className="bg-indigo-700 hover:bg-indigo-800 text-white" onClick={save} disabled={busy} data-testid="lx-save">
          <Save className="h-4 w-4 mr-1" />{busy ? "Saving…" : "Save"}
        </Button>
      </div>

      <div className="space-y-5">
        <section className="border border-slate-200 rounded-xl p-4">
          <div className="font-semibold text-sm mb-3">Story scenes</div>
          <div className="space-y-3">
            {c.scenes.map((sc, i) => (
              <div key={sc.key || i} className="border border-slate-100 rounded-lg p-3" data-testid={`lx-scene-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] uppercase tracking-wider text-slate-400">Scene {i + 1} · {sc.kind}</span>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => moveScene(i, -1)} disabled={i === 0} className="px-1.5 py-0.5 rounded border border-slate-200 text-[11px] disabled:opacity-30" data-testid={`lx-scene-up-${i}`}>↑</button>
                      <button onClick={() => moveScene(i, 1)} disabled={i === c.scenes.length - 1} className="px-1.5 py-0.5 rounded border border-slate-200 text-[11px] disabled:opacity-30" data-testid={`lx-scene-down-${i}`}>↓</button>
                    </div>
                    <label className="flex items-center gap-2 text-[12px]">Active
                      <Switch checked={sc.active !== false} onCheckedChange={(v) => upScene(i, "active", v)} data-testid={`lx-scene-active-${i}`} />
                    </label>
                  </div>
                </div>
                <Label className="text-[12px]">Headline</Label>
                <Input value={sc.headline || ""} onChange={(e) => upScene(i, "headline", e.target.value)} data-testid={`lx-scene-headline-${i}`} />
                <Label className="text-[12px] mt-2 block">Supporting line</Label>
                <Input value={sc.support || ""} onChange={(e) => upScene(i, "support", e.target.value)} data-testid={`lx-scene-support-${i}`} />
                <Label className="text-[12px] mt-2 block">Background image URL (optional)</Label>
                <Input value={sc.media_url || ""} onChange={(e) => upScene(i, "media_url", e.target.value)} placeholder="https://…  (paste an image URL)" data-testid={`lx-scene-media-${i}`} />
                {sc.media_url && <img src={sc.media_url} alt="" className="mt-2 h-16 rounded border border-slate-200 object-cover" onError={(e) => { e.target.style.display = "none"; }} />}
              </div>
            ))}
          </div>
        </section>

        <section className="border border-slate-200 rounded-xl p-4">
          <div className="font-semibold text-sm mb-3">Brand ribbon</div>
          <label className="flex items-center gap-2 text-[13px] mb-2">
            <Switch checked={c.show_ribbon} onCheckedChange={(v) => setC({ ...c, show_ribbon: v })} data-testid="lx-ribbon-toggle" /> Show brand ribbon
          </label>
          <div className="flex gap-3 items-center mb-2 text-[13px]">
            <label className="flex items-center gap-1"><input type="radio" checked={c.brand_mode === "master"} onChange={() => setC({ ...c, brand_mode: "master" })} data-testid="lx-brand-master" /> From Brand Master</label>
            <label className="flex items-center gap-1"><input type="radio" checked={c.brand_mode === "custom"} onChange={() => setC({ ...c, brand_mode: "custom" })} data-testid="lx-brand-custom" /> Custom list</label>
          </div>
          {c.brand_mode === "custom" ? (
            <>
              <Label className="text-[12px]">Brands (comma-separated)</Label>
              <Input value={(c.brands || []).join(", ")} onChange={(e) => setC({ ...c, brands: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} data-testid="lx-brands-input" />
            </>
          ) : (
            <div className="text-[12px] text-slate-500">Using {c.master_brands?.length || 0} active brands from Brand Master: {(c.master_brands || []).slice(0, 12).join(" · ")}</div>
          )}
        </section>

        <section className="border border-slate-200 rounded-xl p-4">
          <div className="font-semibold text-sm mb-3">Proof strip</div>
          <label className="flex items-center gap-2 text-[13px] mb-2">
            <Switch checked={c.show_proof} onCheckedChange={(v) => setC({ ...c, show_proof: v })} data-testid="lx-proof-toggle" /> Show proof strip
          </label>
          <Label className="text-[12px]">Statements (comma-separated, max 3 shown)</Label>
          <Input value={(c.proof || []).join(", ")} onChange={(e) => setC({ ...c, proof: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} data-testid="lx-proof-input" />
        </section>

        <section className="border border-slate-200 rounded-xl p-4">
          <div className="font-semibold text-sm mb-2">Motion level</div>
          <select className="h-9 border border-slate-200 rounded px-2 text-[13px] bg-white" value={c.motion} onChange={(e) => setC({ ...c, motion: e.target.value })} data-testid="lx-motion">
            <option value="premium">Premium (default)</option>
            <option value="subtle">Subtle (slower)</option>
            <option value="off">Off (static)</option>
          </select>
          <p className="text-[11px] text-slate-400 mt-2">Reduced-motion visitors always get a static experience regardless of this setting.</p>
        </section>
      </div>
    </div>
  );
}
